<script setup lang="ts">
import { Form, router } from "@inertiajs/vue3"
import { computed, ref } from "vue"

import FileInput from "@/components/custom/FileInput.vue"
import FormField from "@/components/custom/FormField.vue"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { FieldSet } from "@/components/ui/field"
import { dateFormatter } from "@/lib/formatters"
import { extensionInsurancePath } from "@/routes"
import type { InsurancesShow } from "@/types/serializers"

const props = defineProps<InsurancesShow["extension"]>()

const showFormDialog = ref(false)

const status = computed(() => {
  if (props.is_deactivated) return "cancelado desde"
  if (props.is_deactivate_pending) return "pendiente de desactivación"
  if (props.is_active) return "activo desde"
  if (props.is_activate_pending) return "activación pendiente"
  return "cancelado"
})

function cancelExtension() {
  router.visit(extensionInsurancePath(), {
    method: "delete",
  })
}
</script>

<template>
  <article class="insurance-ext" aria-label="Tarjeta seguro Adeslas Extra">
    <div class="ext-left">
      <div class="ext-left-content w-min text-pink-700">
        <p class="text-2xl leading-5">Adeslas</p>
        <p class="text-3xl font-bold">EXTRA</p>
        <p class="text-pink-900/60">
          {{ status }}
          <template v-if="active_until">
            {{ dateFormatter(active_until, {}, "date") }}
          </template>
        </p>
      </div>
      <div class="ext-left-notch" />
    </div>

    <ul class="ext-center space-y-1 text-gray-700">
      <li>Copago 0€ en todas las consultas</li>
      <li>Dentista premium con hasta 500€ al año</li>
      <li>Fisioterapia ilimitada sin prescripción</li>
      <li>Psicología y nutrición con 10 sesiones al año</li>
      <li>Urgencias 24/7 a domicilio</li>
    </ul>

    <div class="ext-right">
      <p class="text-neutral-700">Prima (que TÚ pagas)</p>
      <div>
        <p class="text-3xl leading-4 font-bold">
          39.90<span class="text-xl text-neutral-600">€/mes</span>
        </p>
        <p class="text-sm leading-4 text-neutral-600">
          Descuento automático en nómina
        </p>
      </div>

      <Button
        v-if="props.can_deactivate"
        variant="destructive"
        size="lg"
        @click="cancelExtension"
      >
        Desactivar
      </Button>

      <Dialog :open="showFormDialog" @update:open="showFormDialog = $event">
        <DialogTrigger>
          <Button v-if="props.can_activate" size="lg">Contratar</Button>
        </DialogTrigger>
        <DialogContent class="max-w-md">
          <DialogHeader>
            <DialogTitle>Contratar ADESLAS EXTRA</DialogTitle>
            <DialogDescription>
              Confirma que deseas ampliar tu cobertura. Se descontarán
              39,90€/mes de tu nómina.
            </DialogDescription>
          </DialogHeader>
          <Form
            :action="extensionInsurancePath()"
            method="POST"
            class="flex w-full flex-col gap-2"
            @success="showFormDialog = false"
            v-slot="{ errors, processing }"
          >
            <FieldSet :disabled="processing">
              <FormField
                name="id_card_front"
                type="file"
                label="DNI Anverso (foto)"
                :errors="errors.id_card_front"
                required
              >
                <FileInput name="id_card_front" accept="image/*" />
              </FormField>

              <FormField
                name="id_card_back"
                type="file"
                label="DNI Reverso (foto)"
                :errors="errors.id_card_back"
                required
              >
                <FileInput name="id_card_back" accept="image/*" />
              </FormField>

              <Button size="lg" class="w-fit" type="submit">Enviar</Button>
            </FieldSet>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  </article>
</template>

<style scoped>
.insurance-ext {
  width: 100%;
  overflow: hidden;
  background: var(--color-pink-50);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 1rem;
  gap: 1rem;
}

.ext-left {
  width: min-content;
}

.ext-left,
.ext-right {
  width: fit-content;
  display: flex;
  align-items: center;
  padding: 1.5rem;
  background: var(--color-pink-200);
  flex-shrink: 0;
  height: 100%;
}

.ext-right {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

.ext-center {
  justify-content: flex-start;
  flex-grow: 1;
  padding: 0.75rem 0;
}

.ext-left-notch {
  display: none;
}
</style>
